import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Star, Zap, Cpu, Briefcase, 
  LayoutGrid, BarChart, ArrowRight, Clock, PanelLeft, X
} from 'lucide-react';
import Header from '../component/Header';
import Footer from '../component/Footer';

const Library = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  
  const actionColorScheme = 'bg-zinc-900 border border-zinc-900 text-white hover:bg-white hover:border-[#09090B] hover:text-[#09090B] transition-all duration-300';

  const categories = [
    { value: 'All', title: 'All Courses', icon: LayoutGrid },
    { value: 'Operations', title: 'Generative AI for Leaders', icon: Zap },
    { value: 'Branding', title: 'Use AI and ChatBots to Automate', icon: Star },
    { value: 'Leadership', title: 'AI Fundamentals for Professionals', icon: Briefcase },
    { value: 'Fundamentals', title: 'AI for Operations Management', icon: Cpu },
  ];

  const courses = [
    {
      id: 1,
      title: 'Generative AI for Leaders',
      category: 'Operations',
      duration: '8.5h',
      level: 'Intermediate',
      image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=800&q=80',
      price: '$47',
      description: 'The world of leadership is changing—fast. Generative AI is reshaping how leaders communicate and scale their impact.' 
    },
    {
      id: 2,
      title: 'AI Fundamentals for Professionals',
      category: 'Leadership',
      duration: '4.5h',
      level: 'Advanced',
      image: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=800&q=80',
      price: '$97',
      description: 'Explore fundamental concepts and strategic implications of AI to harness potential for innovation and efficiency.'
    },
    {
        id: 3,
        title: 'Use AI and ChatBots to Automate',
        category: 'Operations',
        duration: '5.5h',
        level: 'Intermediate',
        image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&q=80',
        price: '$67',
        description: 'Unlock potential in your business. Leverage AI Automation and Content Creation tools to boost productivity.'
    },
    {
        id: 4,
        title: 'AI for Operations Management',
        category: 'Leadership',
        duration: '3.5h',
        level: 'All Levels',
        image: 'https://images.unsplash.com/photo-1507146426996-ef05306b995a?auto=format&fit=crop&w=800&q=80',
        price: '$47',
        description: 'Comprehensive overview of key operations principles and how AI provides significant advantages across the value chain.'
    }
  ];

  const filteredCourses = activeCategory === 'All' 
    ? courses 
    : courses.filter(course => course.category === activeCategory);

  // Get the active title for the mobile button
  const activeCategoryObj = categories.find(cat => cat.value === activeCategory);
  const activeTitle = activeCategoryObj ? activeCategoryObj.title : 'All Courses';

  // Prevent background scrolling when mobile sidebar is open
  useEffect(() => {
    if (isSidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; }
  }, [isSidebarOpen]);

  return (
    <>
      <Header />
      <div className="bg-[#fcfaf8] min-h-screen font-sans text-zinc-900">
        
        {/* Header Area */}
        <div className="pt-24 pb-12 px-6 border-b border-zinc-200 bg-white">
            <div className="max-w-7xl mx-auto">
                <h1 className="text-4xl md:text-5xl font-black text-zinc-900 tracking-tight">
                    Welcome back, <span className="text-amber-500">User</span>
                </h1>
                <p className="text-zinc-500 mt-2 font-medium">Continue your path to AI mastery.</p>
            </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-12 relative">
          
          {/* Mobile Sidebar Toggle Button */}
          <div className="lg:hidden mb-8">
            <button 
              onClick={() => setIsSidebarOpen(true)}
              className="w-full flex items-center justify-between bg-white border border-zinc-200 p-4 rounded-2xl shadow-sm hover:border-amber-400 transition-colors"
            >
              <div className="flex items-center gap-3 shrink-0">
                <PanelLeft className="w-5 h-5 text-zinc-500" />
                <span className="font-bold text-zinc-900">Sidebar</span>
              </div>
              <div className="bg-zinc-100 px-3 py-1 rounded-lg text-sm font-bold truncate max-w-[140px] sm:max-w-[250px]">
                {activeTitle}
              </div>
            </button>
          </div>

          <div className="flex flex-col lg:flex-row gap-12">
            
            {/* MOBILE BACKDROP */}
            {isSidebarOpen && (
              <div 
                className="fixed inset-0 bg-zinc-950/60 backdrop-blur-sm z-40 lg:hidden transition-opacity"
                onClick={() => setIsSidebarOpen(false)}
              />
            )}

            {/* STICKY SIDEBAR (Desktop) / SLIDE-OUT DRAWER (Mobile) */}
            <aside className={`fixed inset-y-0 left-0 z-50 w-[300px] h-full overflow-y-auto bg-[#fcfaf8] transform transition-transform duration-300 ease-in-out lg:bg-transparent lg:relative lg:translate-x-0 lg:z-auto lg:h-auto lg:overflow-visible lg:w-[280px] lg:shrink-0 ${
              isSidebarOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full'
            }`}>
              <div className="p-6 lg:p-0 lg:sticky lg:top-8 space-y-6">
                
                {/* Mobile Drawer Header */}
                <div className="flex items-center justify-between lg:hidden mb-2">
                  <h2 className="font-black text-xl text-zinc-900 tracking-tight">Sidebar</h2>
                  <button 
                    onClick={() => setIsSidebarOpen(false)} 
                    className="p-2 bg-white border border-zinc-200 rounded-full hover:bg-zinc-100 transition-colors"
                  >
                    <X className="w-5 h-5 text-zinc-500" />
                  </button>
                </div>

                {/* INTERACTIVE WHITE MENU BOX */}
                <div className="bg-white border border-zinc-200 rounded-[2rem] p-6 shadow-xl shadow-zinc-200/40">
                    <h3 className="hidden lg:block text-xs font-black uppercase tracking-widest text-zinc-400 mb-6 px-2">
                        Categories
                    </h3>
                    
                    <nav className="flex flex-col gap-2">
                        {categories.map((cat) => {
                            const Icon = cat.icon;
                            const isActive = activeCategory === cat.value;
                            
                            return (
                                <button
                                    key={cat.value}
                                    onClick={() => {
                                      setActiveCategory(cat.value);
                                      setIsSidebarOpen(false); // Close mobile sidebar on selection
                                    }}
                                    className={`group flex items-center justify-between px-4 py-3.5 rounded-xl font-bold text-sm transition-all duration-300 ${
                                        isActive 
                                        ? 'bg-[#09090B] text-white shadow-md shadow-[#09090B]-500/20 translate-x-1' 
                                        : 'text-zinc-500 hover:bg-zinc-50 hover:text-zinc-900 hover:translate-x-1 hover:border-zinc-200 border border-transparent'
                                    }`}
                                >
                                    <div className="flex items-center gap-3 relative z-10 text-left">
                                        <Icon className={`w-4 h-4 transition-colors duration-300 ${isActive ? 'text-white' : 'text-zinc-400 group-hover:text-[#09090B]'}`} />
                                        {cat.title}
                                    </div>
                                    
                                    {/* Animated slide-in arrow */}
                                    <ArrowRight className={`w-4 h-4 transition-all duration-300 ${
                                        isActive 
                                        ? 'opacity-100 translate-x-0 text-white' 
                                        : 'opacity-0 -translate-x-4 text-[#09090B] group-hover:opacity-100 group-hover:translate-x-0'
                                    }`} />
                                </button>
                            );
                        })}
                    </nav>
                </div>
              </div>
            </aside>

            {/* MAIN CONTENT AREA */}
            <main className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-xl font-bold text-zinc-900">
                    Showing {activeTitle} <span className="text-zinc-400 font-medium ml-2 text-sm">({filteredCourses.length} courses)</span>
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {filteredCourses.map((course) => (
                  <div 
                    key={course.id} 
                    className="bg-white border border-zinc-200 rounded-[2rem] overflow-hidden hover:border-amber-400 hover:shadow-2xl hover:shadow-amber-500/10 transition-all duration-500 group flex flex-col h-full"
                  >
                    {/* Thumbnail */}
                    <div className="h-52 md:h-44 lg:h-52 relative overflow-hidden shrink-0">
                      <img 
                        src={course.image} 
                        alt={course.title} 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" 
                      />
            
                    </div>

                    {/* Content */}
                    <div className="p-8 flex flex-col flex-1">
                  
                      
                      <p className="text-l font-black text-zinc-900 mb-3 leading-tight transition-colors">
                        {course.description}
                      </p>
                      
         
                    </div>
                  </div>
                ))}
              </div>
            </main>

          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Library;